import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';

import { Button, Icon, Input, Tooltip } from 'components/common';
import { DisabledButtonWrapper } from 'components/common/styled';
import { usePost, useAuth } from 'hooks';
import { enableList, disableList } from 'services/api';
import { setModal, toggleActiveList, setMembersSearch } from 'actions/lists';
import {
  getActiveList,
  getMembersSearch,
  getSelectedMembers,
  getListMembers,
} from 'selectors/lists';

import { Container, Buttons } from './styled';
import { AddToList } from '../Members/Actions';
import { noPermissions, views } from '../constants';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import Export from 'components/Export';
import { downloadType } from 'components/Export/Summary/constants';
import useExport from '../Actions/useExport';
import { getSelectedCount } from 'components/common/Table/MultiSelect/helpers';
import { Dropdown } from 'antd';
import * as permissionsHelper from '../Items/permissionsHelper';
import { crmTypes } from 'utils/helpers/crm';

const Actions = ({
  onPushToCRM,
  loading,
  view,
  style = {},
  placeholder = 'Search for a Contact',
}) => {
  const dispatch = useDispatch();
  const activeList = useSelector(getActiveList);
  const search = useSelector(getMembersSearch);
  const listMembers = useSelector(getListMembers);
  const totalMembers = listMembers?.total ?? 0;
  const selectedMembers = useSelector(getSelectedMembers);
  const selectedCount = useMemo(
    () => getSelectedCount(totalMembers, selectedMembers),
    [selectedMembers, totalMembers]
  );

  const [getExportBody, handleExport] = useExport(view);

  const { user } = useAuth();

  const [{ res: enableData, loading: enableLoading }, postEnable] = usePost({
    url: enableList(activeList?.id, view),
  });
  const [{ res: disableData, loading: disableLoading }, postDisable] = usePost({
    url: disableList(activeList?.id, view),
  });

  useEffect(() => {
    if (enableData) {
      dispatch(toggleActiveList(true));
    }
  }, [enableData, dispatch]);

  useEffect(() => {
    if (disableData) {
      dispatch(toggleActiveList(false));
    }
  }, [disableData, dispatch]);

  const handleTogglePause = () => {
    if (activeList.enabled) {
      postDisable();
    } else {
      postEnable();
    }
  };

  const [localSearchValue, handleSearch] = useDebouncedSearch(
    search,
    setMembersSearch
  );

  const disableButtons =
    !activeList.id || loading || enableLoading || disableLoading;

  const canModify = permissionsHelper.canModify(activeList, user);
  const canModifyMembers = permissionsHelper.canModifyMembers(activeList, user);

  const deleteOptions = useMemo(() => {
    const options = [];
    if (canModify) {
      options.push({
        label: (
          <div onClick={() => dispatch(setModal('delete'))}>Delete List</div>
        ),
        key: 1,
      });
    }
    if (selectedCount > 0 && canModifyMembers) {
      options.unshift({
        label: (
          <div onClick={() => dispatch(setModal('removeFrom'))}>
            Remove Selected{' '}
            {view === views.organisations
              ? `Organisation${selectedCount > 1 ? 's' : ''}`
              : `Contact${selectedCount > 1 ? 's' : ''}`}
          </div>
        ),
        key: 2,
      });
    }
    return options;
  }, [selectedCount, view, dispatch, canModify, canModifyMembers]);

  const DropdownWrapper =
    deleteOptions.length <= 0 ? DisabledButtonWrapper : Fragment;
  const ButtonWrapper = !canModify ? DisabledButtonWrapper : Fragment;
  return (
    <Container style={style}>
      <Input
        prefix={<Icon icon='keywords' color='rgba(0, 0, 0, 0.85)' size='1x' />}
        placeholder={placeholder}
        className='search-input'
        style={{ width: 250 }}
        onChange={handleSearch}
        value={localSearchValue}
        allowClear
      />
      {selectedCount > 0 && (
        <div style={{ marginLeft: 10, fontSize: 13 }}>
          {selectedCount} Selected
        </div>
      )}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Buttons>
          <Tooltip title={deleteOptions.length > 0 ? 'Delete' : noPermissions}>
            <DropdownWrapper>
              {deleteOptions.length === 1 ? (
                <Button
                  size='small'
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={deleteOptions[0].label.props.onClick}
                  disabled={disableButtons}
                >
                  <Icon
                    icon='trash'
                    size='1x'
                    style={{ color: 'currentColor' }}
                  />
                </Button>
              ) : (
                <Dropdown
                  menu={{
                    items: deleteOptions,
                  }}
                  trigger={['click']}
                  disabled={disableButtons || deleteOptions.length <= 0}
                >
                  <Button
                    size='small'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Icon
                      icon='trash'
                      size='1x'
                      style={{ color: 'currentColor' }}
                    />
                  </Button>
                </Dropdown>
              )}
            </DropdownWrapper>
          </Tooltip>
          <Tooltip
            title={
              canModify
                ? activeList?.enabled
                  ? 'Pause List'
                  : 'Resume List'
                : noPermissions
            }
          >
            <ButtonWrapper>
              <Button
                disabled={disableButtons || !canModify}
                size='small'
                onClick={handleTogglePause}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {activeList?.enabled ? (
                  <PauseCircleOutlined style={{ height: '14px' }} />
                ) : (
                  <PlayCircleOutlined style={{ height: '14px' }} />
                )}
              </Button>
            </ButtonWrapper>
          </Tooltip>
          <Tooltip title={canModify ? 'List Settings' : noPermissions}>
            <ButtonWrapper>
              <Button
                disabled={disableButtons || !canModify}
                size='small'
                onClick={() => dispatch(setModal('edit'))}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon
                  icon='settings'
                  size='1x'
                  style={{ color: 'currentColor' }}
                />
              </Button>
            </ButtonWrapper>
          </Tooltip>
          <AddToList
            onCreateNewItemList={() => {
              dispatch(setModal('createAndAdd'));
            }}
            disabled={disableButtons || totalMembers <= 0}
            view={view}
            placeholder={`Copy ${
              selectedCount <= 0 ? 'All' : 'Selected'
            } to a Different List`}
            width={selectedCount <= 0 ? 200 : 240}
          />
          {view === views.researchers &&
            permissionsHelper.canPushToCrm(
              activeList,
              user,
              crmTypes.SALESFORCE
            ) && (
              <Button
                disabled={disableButtons || totalMembers <= 0}
                size='small'
                onClick={() => onPushToCRM(crmTypes.SALESFORCE)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon
                  icon='salesforce'
                  size='1x'
                  style={{ marginRight: '8px', color: 'currentColor' }}
                />
                Push{selectedCount <= 0 ? '' : ' Selected'} to Salesforce
              </Button>
            )}
          {view === views.researchers &&
            permissionsHelper.canPushToCrm(
              activeList,
              user,
              crmTypes.HUBSPOT
            ) && (
              <Button
                disabled={disableButtons || totalMembers <= 0}
                size='small'
                onClick={() => onPushToCRM(crmTypes.HUBSPOT)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon
                  icon='hubspot'
                  size='1x'
                  style={{ marginRight: '8px', color: 'currentColor' }}
                />
                Push{selectedCount <= 0 ? '' : ' Selected'} to HubSpot
              </Button>
            )}
          {permissionsHelper.hasPermissionToPushExport(user) && (
            <Export
              downloadType={
                view === views.organisations
                  ? downloadType.organisationList
                  : downloadType.researcherList
              }
              getQueryBody={getExportBody}
              disabled={disableButtons || totalMembers <= 0}
              onExport={handleExport}
              canUseCredits={view === views.researchers}
            >
              Export {selectedCount <= 0 ? 'All' : 'Selected'}{' '}
              {view === views.organisations
                ? `Organisation${
                    selectedCount > 1 || selectedCount <= 0 ? 's' : ''
                  }`
                : `Contact${
                    selectedCount > 1 || selectedCount <= 0 ? 's' : ''
                  }`}
            </Export>
          )}
        </Buttons>
      </div>
    </Container>
  );
};

export default Actions;
